/**
 * Hydrawise Dashboard Page
 *
 * Monitoring and manual control for Hunter Hydrawise irrigation controllers.
 */

import React, { useEffect, useState } from 'react';
import { useHydrawiseStore } from './hydrawise-store';
import { ApiKeySetupCard } from './components/ApiKeySetupCard';
import { ControllerCard } from './components/ControllerCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Loader2, Droplets, AlertCircle } from 'lucide-react';

const HydrawiseDashboardPage: React.FC = () => {
  const {
    hasApiKey,
    controllers,
    zones,
    isLoading,
    error,
    fetchSettings,
    fetchControllers,
    fetchZones,
    startPolling,
    clearError,
  } = useHydrawiseStore();

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      await fetchSettings();
      setInitialized(true);
    };
    init();
  }, [fetchSettings]);

  // Fetch controllers and zones once API key is confirmed
  useEffect(() => {
    if (initialized && hasApiKey) {
      fetchControllers();
    }
  }, [initialized, hasApiKey, fetchControllers]);

  // Fetch zones for each controller
  useEffect(() => {
    for (const controller of controllers) {
      fetchZones(controller.controllerId);
      startPolling(controller.controllerId);
    }
  }, [controllers, fetchZones, startPolling]);

  const handleRefresh = () => {
    fetchControllers();
    for (const controller of controllers) {
      fetchZones(controller.controllerId);
    }
  };

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Droplets className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Irrigation</h1>
            <p className="text-sm text-muted-foreground">
              Hunter Hydrawise controller management
            </p>
          </div>
        </div>
        {hasApiKey && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {controllers.length} controller{controllers.length !== 1 ? 's' : ''}
            </Badge>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-1">Refresh</span>
            </Button>
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={clearError} className="ml-auto">
            Dismiss
          </Button>
        </div>
      )}

      {/* API Key Setup */}
      {!hasApiKey && <ApiKeySetupCard />}

      {/* Controllers */}
      {hasApiKey && controllers.length === 0 && !isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          <Droplets className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No controllers found.</p>
          <p className="text-xs mt-1">Make sure your Hydrawise account has at least one controller registered.</p>
        </div>
      )}

      {hasApiKey &&
        controllers.map((controller) => (
          <ControllerCard
            key={controller.controllerId}
            controller={controller}
            zones={zones[controller.controllerId] ?? []}
          />
        ))}
    </div>
  );
};

export default HydrawiseDashboardPage;
